import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Paper, Alert, CircularProgress } from '@mui/material';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc } from 'firebase/firestore';
import { ROLES } from '../../firebase.marketplace';

const DirectAdminSetup = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });

    return () => unsubscribe();
  }, []);

  const makeAdmin = async () => {
    if (!user) {
      setError('You must be logged in to perform this action');
      return;
    }

    setLoading(true);
    setMessage(null);
    setError(null);

    try {
      const db = getFirestore();
      const userRef = doc(db, 'users', user.uid);
      
      // Get current user data if it exists
      const userDoc = await getDoc(userRef);
      let userData = {};
      
      if (userDoc.exists()) {
        userData = userDoc.data();
      }
      
      // Update with admin roles
      await setDoc(userRef, {
        ...userData,
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || user.email.split('@')[0],
        photoURL: user.photoURL || null,
        roles: {
          [ROLES.USER]: true,
          [ROLES.ADMIN]: true,
          [ROLES.ARTIST]: true,
          superadmin: true
        },
        updatedAt: new Date()
      }, { merge: true });
      
      setMessage(`Successfully made ${user.email} a super admin!`);
    } catch (err) {
      console.error('Error making admin:', err);
      setError(err.message || 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 4, maxWidth: 600, mx: 'auto' }}>
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom align="center">
          Direct Admin Setup
        </Typography>
        
        {user ? (
          <>
            <Typography variant="body1" paragraph>
              Currently logged in as: <strong>{user.email}</strong>
            </Typography>
            
            <Typography variant="body1" paragraph>
              Click the button below to make this account a super admin with full permissions.
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button 
                variant="contained" 
                color="primary"
                disabled={loading}
                onClick={makeAdmin}
              >
                {loading ? <CircularProgress size={24} /> : 'Make Super Admin'}
              </Button>
            </Box>
          </>
        ) : (
          <Alert severity="warning">
            You must be logged in to perform this action. Please sign in first.
          </Alert>
        )}
        
        {message && (
          <Alert severity="success" sx={{ mt: 3 }}>
            {message}
          </Alert>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {error}
          </Alert>
        )}
      </Paper>
      
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Troubleshooting
        </Typography>
        
        <Typography variant="body2" paragraph>
          If you're still having permission issues, try these steps:
        </Typography>
        
        <ol>
          <li>Make sure you're logged in with the correct account</li>
          <li>Check that your Firebase security rules allow write access to the users collection</li>
          <li>Try signing out and signing back in before making this request</li>
          <li>Check your browser console for any specific error messages</li>
        </ol>
        
        <Typography variant="body2" sx={{ mt: 2 }}>
          After becoming an admin, you can access the admin panel at <code>/marketplace/admin/tools</code>
        </Typography>
      </Paper>
    </Box>
  );
};

export default DirectAdminSetup;
